import { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Avatar,
  Paper,
  Stack,
  Tooltip,
  Chip,
} from "@mui/material";
import { m, useMotionValue, useTransform, animate } from "framer-motion";
import Iconify from "src/components/iconify";
import CustomCircularLoading from "src/components/custom-circular-loading/custom-circular-loading";
import { Seller } from "src/types/sellers";
import {
  RankedPosition,
  getTopThreePositions,
  getRemainingPositions,
} from "../utils/ranking-helpers";

// ----------------------------------------------------------------------

// Helper para extrair as duas primeiras letras do nome
const getInitials = (name: string) => {
  return name.substring(0, 2).toUpperCase();
};

// ----------------------------------------------------------------------

// Componente para mostrar múltiplos sellers empatados
function TiedSellers({
  sellers,
  max = 3,
}: {
  sellers: Seller[];
  max?: number;
}) {
  const displaySellers = sellers.slice(0, max);
  const remainingSellers = sellers.slice(max);
  const hasMore = remainingSellers.length > 0;

  if (sellers.length === 1) {
    return <>{sellers[0].name}</>;
  }

  return (
    <Box component="span" sx={{ display: "inline" }}>
      {displaySellers.map((seller, idx) => (
        <span key={seller._id}>
          {seller.name}
          {idx < displaySellers.length - 1 && ", "}
        </span>
      ))}
      {hasMore && (
        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  display: "block",
                  mb: 1,
                  fontSize: "0.875rem",
                }}
              >
                Todos os empatados nesta posição:
              </Typography>
              {sellers.map((seller) => (
                <Typography
                  key={seller._id}
                  variant="body2"
                  sx={{ display: "block", fontSize: "0.8125rem", mb: 0.25 }}
                >
                  • {seller.name}
                </Typography>
              ))}
            </Box>
          }
          arrow
        >
          <Chip
            label={`+${remainingSellers.length} mais`}
            size="small"
            sx={{
              ml: 0.5,
              height: 20,
              fontSize: "0.7rem",
              cursor: "pointer",
              verticalAlign: "middle",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.3)",
              },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------

function AnimatedCounter({ value }: { value: number }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, Math.round);

  useEffect(() => {
    const animation = animate(count, value, { duration: 2 });
    return animation.stop;
  }, [count, value]);

  return <m.span>{rounded}</m.span>;
}

// ----------------------------------------------------------------------

function PodiumCard({
  position,
  rankedPosition,
  delay,
}: {
  position: number;
  rankedPosition: RankedPosition;
  delay: number;
}) {
  const heights = {
    1: { xs: 160, sm: 280 },
    2: { xs: 130, sm: 220 },
    3: { xs: 110, sm: 200 },
  };

  const colors = {
    1: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
    2: "linear-gradient(135deg, #E8E8E8 0%, #C0C0C0 100%)",
    3: "linear-gradient(135deg, #FFE4B5 0%, #CD7F32 100%)",
  };

  const solidColors = {
    1: "#FFA500", // Dourado
    2: "#C0C0C0", // Prata
    3: "#CD7F32", // Bronze
  };

  const icons = {
    1: "🏆",
    2: "🥈",
    3: "🥉",
  };

  const labels = {
    1: "1º LUGAR",
    2: "2º LUGAR",
    3: "3º LUGAR",
  };

  const isTied = rankedPosition.sellers.length > 1;

  return (
    <m.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay, duration: 0.6, type: "spring" }}
    >
      <Stack
        alignItems="center"
        spacing={2}
        sx={{
          position: "relative",
          zIndex: position === 1 ? 3 : position === 2 ? 2 : 1,
        }}
      >
        {/* Avatar e badge */}
        <Box sx={{ position: "relative" }}>
          <Avatar
            sx={{
              width: position === 1 ? { xs: 64, sm: 100 } : { xs: 52, sm: 80 },
              height: position === 1 ? { xs: 64, sm: 100 } : { xs: 52, sm: 80 },
              fontSize:
                position === 1 ? { xs: 26, sm: 40 } : { xs: 20, sm: 32 },
              border: "4px solid white",
              boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
              background: colors[position as keyof typeof colors],
              color: "#fff",
              fontWeight: "bold",
            }}
          >
            {position}
          </Avatar>
          <Box
            sx={{
              position: "absolute",
              top: -10,
              right: -10,
              width: { xs: 26, sm: 40 },
              height: { xs: 26, sm: 40 },
              borderRadius: "50%",
              background: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: { xs: 14, sm: 24 },
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            }}
          >
            {icons[position as keyof typeof icons]}
          </Box>
          {isTied && (
            <Chip
              label={`${rankedPosition.sellers.length} empatados`}
              size="small"
              sx={{
                position: "absolute",
                bottom: -12,
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: "0.65rem",
                height: 18,
                backgroundColor:
                  solidColors[position as keyof typeof solidColors],
                color: "white",
                fontWeight: "bold",
              }}
            />
          )}
        </Box>

        {/* Pedestal */}
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            width: { xs: 90, sm: 140 },
            height: heights[position as keyof typeof heights],
            background: colors[position as keyof typeof colors],
            borderRadius: "12px 12px 0 0",
            position: "relative",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            overflow: "hidden",
            pb: 2,
          }}
        >
          {/* Confetes decorativos */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: position === 1 ? 120 : position === 2 ? 100 : 80,
              opacity: 0.2,
              background:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Cpath d='M0 20 L20 0 L40 20 L20 40 Z' fill='white'/%3E%3C/svg%3E\")",
              backgroundSize: "20px 20px",
            }}
          />

          <Typography
            variant="caption"
            sx={{
              color: "#fff",
              fontWeight: "bold",
              fontSize: { xs: 8, sm: 11 },
              letterSpacing: { xs: 0.5, sm: 1.5 },
              mb: 0.5,
            }}
          >
            {labels[position as keyof typeof labels]}
          </Typography>

          <Box
            sx={{
              color: "#fff",
              fontWeight: "bold",
              textAlign: "center",
              px: 0.5,
              fontSize:
                position === 1 ? { xs: 11, sm: 18 } : { xs: 10, sm: 16 },
            }}
          >
            <TiedSellers sellers={rankedPosition.sellers} max={3} />
          </Box>

          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: "bold",
              mt: 1,
              fontSize: { xs: 12, sm: "inherit" },
              textAlign: "center",
              mx: "4px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            <AnimatedCounter value={rankedPosition.userCount} /> usuários
            vincularam
          </Typography>
        </Stack>
      </Stack>
    </m.div>
  );
}

// ----------------------------------------------------------------------

const PODIUM_COLORS = {
  1: {
    gradient: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
    solid: "#FFA500",
    icon: "🏆",
  },
  2: {
    gradient: "linear-gradient(135deg, #E8E8E8 0%, #C0C0C0 100%)",
    solid: "#C0C0C0",
    icon: "🥈",
  },
  3: {
    gradient: "linear-gradient(135deg, #FFE4B5 0%, #CD7F32 100%)",
    solid: "#CD7F32",
    icon: "🥉",
  },
};

function MobileRankingItem({
  rankedPosition,
  index,
}: {
  rankedPosition: RankedPosition;
  index: number;
}) {
  const position = rankedPosition.position;
  const podium = PODIUM_COLORS[position as keyof typeof PODIUM_COLORS];
  const isPodium = position <= 3;
  const isTied = rankedPosition.sellers.length > 1;

  return (
    <m.div
      initial={{ x: -50, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: 0.1 + index * 0.08, duration: 0.4 }}
    >
      <Paper
        sx={{
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          borderRadius: 2,
          overflow: "hidden",
          border: "none",
          boxShadow: isPodium ? "0 4px 16px rgba(0,0,0,0.12)" : undefined,
          transition: "all 0.3s",
          background: isPodium ? podium.gradient : undefined,
        }}
      >
        {/* Posição */}
        <Box
          sx={{
            width: 48,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 1.5,
            px: 1,
            position: "relative",
          }}
        >
          {isPodium ? (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              {podium.icon}
            </Box>
          ) : (
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                background: (theme) => theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: 16,
                color: "white",
              }}
            >
              {position}
            </Box>
          )}
          {isTied && (
            <Chip
              label={rankedPosition.sellers.length}
              size="small"
              sx={{
                position: "absolute",
                bottom: 4,
                fontSize: "0.6rem",
                height: 16,
                minWidth: 16,
                backgroundColor: "warning.main",
                color: "white",
                fontWeight: "bold",
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
          )}
        </Box>

        {/* Nome */}
        <Box sx={{ flex: 1, py: 1.5, px: 0.5 }}>
          <Box
            sx={{
              color: isPodium ? "#fff" : "text.primary",
              fontWeight: "700",
              fontSize: "0.9375rem",
            }}
          >
            <TiedSellers sellers={rankedPosition.sellers} max={2} />
          </Box>
        </Box>

        {/* Contagem */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.5,
            py: 1,
            mr: 1,
            borderRadius: 2,
            background: isPodium ? "rgba(255,255,255,0.25)" : "primary.lighter",
            bgcolor: !isPodium ? "primary.lighter" : undefined,
          }}
        >
          <Iconify
            icon="mdi:account-group"
            width={18}
            color={isPodium ? "#fff" : undefined}
          />
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{ color: isPodium ? "#fff" : "primary.darker" }}
          >
            <AnimatedCounter value={rankedPosition.userCount} />
          </Typography>
        </Box>
      </Paper>
    </m.div>
  );
}

// ----------------------------------------------------------------------

function RankingListItem({
  rankedPosition,
  index,
}: {
  rankedPosition: RankedPosition;
  index: number;
}) {
  const position = rankedPosition.position;
  const isTied = rankedPosition.sellers.length > 1;

  return (
    <m.div
      initial={{ x: -50, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: 0.8 + index * 0.1, duration: 0.4 }}
    >
      <Paper
        sx={{
          p: 2,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderRadius: 2,
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, transparent 100%)",
          border: "1px solid",
          borderColor: "divider",
          transition: "all 0.3s",
          "&:hover": {
            transform: "translateX(8px)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderColor: "primary.main",
          },
        }}
      >
        {/* Posição */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: (theme) => theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            fontSize: 18,
            color: "white",
            position: "relative",
          }}
        >
          {position}
          {isTied && (
            <Chip
              label={rankedPosition.sellers.length}
              size="small"
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                fontSize: "0.65rem",
                height: 18,
                minWidth: 18,
                backgroundColor: "warning.main",
                color: "white",
                fontWeight: "bold",
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
          )}
        </Box>

        {/* Nome */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontWeight: "600", fontSize: "1rem" }}>
            <TiedSellers sellers={rankedPosition.sellers} max={3} />
          </Box>
        </Box>

        {/* Pontos */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: "primary.lighter",
          }}
        >
          <Iconify icon="mdi:account-group" width={20} />
          <Typography variant="h6" fontWeight="bold" color="primary.darker">
            <AnimatedCounter value={rankedPosition.userCount} /> usuários
            vincularam
          </Typography>
        </Box>
      </Paper>
    </m.div>
  );
}

// ----------------------------------------------------------------------

interface RankingContentProps {
  rankedSellers: Seller[];
  rankedPositions: RankedPosition[];
  loading: boolean;
  maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl";
}

export default function RankingContent({
  rankedSellers,
  rankedPositions,
  loading,
  maxWidth = "lg",
}: RankingContentProps) {
  const topThreePositions = getTopThreePositions(rankedPositions);
  const remainingPositions = getRemainingPositions(rankedPositions);

  if (loading) {
    return (
      <Container maxWidth={maxWidth}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
          }}
        >
          <CustomCircularLoading isLoading={loading} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={maxWidth}>
      {/* ── MOBILE: lista completa com top-3 estilizado ── */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <m.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Iconify icon="mdi:trophy-outline" width={22} />
            Classificação Geral dos Operadores
          </Typography>
        </m.div>

        {rankedPositions.map((rankedPosition, index) => (
          <MobileRankingItem
            key={`position-${rankedPosition.position}`}
            rankedPosition={rankedPosition}
            index={index}
          />
        ))}
      </Box>

      {/* ── DESKTOP: pódio + lista ── */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        {/* Pódio */}
        <Box sx={{ mb: 8 }}>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="flex-end"
            spacing={3}
            sx={{ mb: 4 }}
          >
            {topThreePositions[1] && (
              <PodiumCard
                position={2}
                rankedPosition={topThreePositions[1]}
                delay={0.2}
              />
            )}
            {topThreePositions[0] && (
              <PodiumCard
                position={1}
                rankedPosition={topThreePositions[0]}
                delay={0}
              />
            )}
            {topThreePositions[2] && (
              <PodiumCard
                position={3}
                rankedPosition={topThreePositions[2]}
                delay={0.4}
              />
            )}
          </Stack>
        </Box>

        {/* Classificação Geral */}
        <Box>
          <m.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Iconify icon="mdi:trophy-outline" width={28} />
              Classificação Geral
            </Typography>
          </m.div>

          <Box>
            {remainingPositions.map((rankedPosition, index) => (
              <RankingListItem
                key={`position-${rankedPosition.position}`}
                rankedPosition={rankedPosition}
                index={index}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
